import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ChannelDTO } from "@justone/shared";
import { errorMessage, fieldError, http } from "../api/http";
import type { NotifyLevel } from "@justone/shared";
import { keys, useChannelAction, useChannels, useMe, useSetNotifyLevel, useUserMap } from "../api/queries";
import { setViewingRoom } from "../api/realtime";
import { channelView, type RoomView } from "../lib/room";
import { Composer } from "../components/Composer";
import { MessageList } from "../components/MessageList";
import { ThreadPanel } from "../components/ThreadPanel";
import { Dialog, Field, Icon, useConfirm, useDismiss, useToast } from "../components/ui";
import { useMenuKeys } from "../components/Popover";
import type { WorkspaceOutlet } from "./Workspace";
import { ChannelTabs } from "../components/ChannelTabs";

export function ChannelPage() {
  const { channelId } = useParams();
  const id = Number(channelId);
  const channels = useChannels();
  const channel = channels.data?.find((c) => c.id === id);
  const me = useMe();
  const users = useUserMap();
  const outlet = useOutletContext<WorkspaceOutlet>();
  const { openNav } = outlet;
  const action = useChannelAction();
  const toast = useToast();
  const navigate = useNavigate();
  const { rootId } = useParams();
  const openThread = useCallback((root: number) => navigate(`/c/${id}/t/${root}`), [navigate, id]);

  // 채널에 들어온 순간의 읽은 위치를 기억해 "새 메시지" 구분선에 쓴다.
  const [readMarker, setReadMarker] = useState<{ id: number; marker: number | null } | null>(null);
  useEffect(() => {
    if (channel && readMarker?.id !== channel.id) setReadMarker({ id: channel.id, marker: channel.unread > 0 ? channel.lastReadMessageId : null });
  }, [channel, readMarker]);

  useEffect(() => {
    setViewingRoom({ kind: "channel", id });
    return () => setViewingRoom(null);
  }, [id]);

  if (!channel) return <Navigate to="/" replace />;
  if (readMarker?.id !== channel.id) return null;

  const join = () => action.mutate({ id: channel.id, action: "join" }, { onError: (e) => toast(errorMessage(e)) });
  const room = channelView(channel, me);

  return (
    <div className={`channel-layout${rootId ? " with-thread" : ""}`}>
      <section className="channel-col" aria-label={`#${channel.name}`}>
        <header className="topbar">
          <button type="button" className="icon-btn menu-toggle" onClick={openNav} aria-label="채널 목록 열기">
            <Icon name="menu" />
          </button>
          <h1>#{channel.name}</h1>
          <ChannelTabs channelId={channel.id} />
          {channel.topic && <span className="topic">{channel.topic}</span>}
          <div className="actions">
            <span className="pill" title="참여한 사람 수">
              {channel.memberCount}명
            </span>
            <ChannelMenu channel={channel} />
          </div>
        </header>
        <MessageList key={`messages-${channel.id}`} room={room} users={users} me={me} readMarker={readMarker.marker} onOpenThread={openThread} />
        {room.canWrite ? (
          <Composer key={`composer-${channel.id}`} room={room} />
        ) : channel.archived ? (
          <div className="composer-blocked">보관된 채널입니다. 읽기만 할 수 있습니다.</div>
        ) : (
          <div className="composer-blocked">
            <span>이 채널에 참여하면 메시지를 쓸 수 있습니다.</span>
            <button type="button" className="btn btn-primary btn-sm" onClick={join} disabled={action.isPending}>
              참여하기
            </button>
          </div>
        )}
      </section>
      <Outlet context={{ ...outlet, room } satisfies ThreadOutlet} />
    </div>
  );
}

export interface ThreadOutlet extends WorkspaceOutlet {
  room: RoomView;
}

/** /c/:channelId/t/:rootId, /d/:conversationId/t/:rootId */
export function ThreadRoute() {
  const { room } = useOutletContext<ThreadOutlet>();
  const { rootId } = useParams();
  const navigate = useNavigate();
  const base = `${room.ref.kind === "channel" ? "/c" : "/d"}/${room.ref.id}`;
  const close = useCallback(() => navigate(base), [navigate, base]);
  return <ThreadPanel key={rootId} room={room} rootId={Number(rootId)} onClose={close} />;
}

function ChannelMenu({ channel }: { channel: ChannelDTO }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const action = useChannelAction();
  const setLevel = useSetNotifyLevel();
  const toast = useToast();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const levels: [NotifyLevel, string][] = [
    ["all", "모든 메시지 알림"],
    ["mention", "멘션만 알림"],
    ["none", "알림 끄기"],
  ];
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss<HTMLDivElement>(open, close);
  useMenuKeys(open, ref);

  const run = (fn: () => Promise<unknown>) => {
    close();
    fn().catch((e) => toast(errorMessage(e)));
  };
  const leave = () =>
    run(async () => {
      await action.mutateAsync({ id: channel.id, action: "leave" });
      navigate("/");
    });
  const archive = (archived: boolean) => run(() => action.mutateAsync({ id: channel.id, action: "archive", body: { archived } }));
  const remove = async () => {
    close();
    const ok = await confirm({
      title: `#${channel.name} 채널 삭제`,
      body: "채널의 메시지·할일·위키가 모두 지워집니다. 되돌릴 수 없습니다. 기록을 남기려면 보관하세요.",
      confirmLabel: "채널 삭제",
      danger: true,
      typeToConfirm: channel.name,
    });
    if (!ok) return;
    run(async () => {
      await http.delete(`/api/channels/${channel.id}`);
      await qc.invalidateQueries({ queryKey: keys.channels });
      navigate("/");
    });
  };

  return (
    <div ref={ref}>
      <button type="button" className="icon-btn" style={{ color: "var(--ink-2)" }} onClick={() => setOpen((o) => !o)} aria-label="채널 메뉴" aria-haspopup="menu" aria-expanded={open}>
        <Icon name="more" />
      </button>
      {open && (
        <div className="menu" role="menu">
          {channel.joined &&
            levels.map(([level, label]) => (
              <button
                key={level}
                type="button"
                role="menuitemradio"
                aria-checked={channel.notifyLevel === level}
                className="menu-check"
                onClick={() => {
                  close();
                  setLevel.mutate({ channelId: channel.id, level }, { onError: (e) => toast(errorMessage(e)) });
                }}
              >
                <span className="mark">{channel.notifyLevel === level && <Icon name="check" />}</span>
                {label}
              </button>
            ))}
          {channel.joined && <hr className="menu-sep" />}
          {(!channel.archived || me.role === "admin") && (
            <button type="button" role="menuitem" onClick={() => (close(), setEditing(true))}>
              이름과 설명 바꾸기
            </button>
          )}
          {channel.joined && !channel.isDefault && (
            <button type="button" role="menuitem" onClick={leave}>
              채널에서 나가기
            </button>
          )}
          {me.role === "admin" && !channel.isDefault && (
            <>
              <button type="button" role="menuitem" onClick={() => archive(!channel.archived)}>
                {channel.archived ? "보관 해제" : "채널 보관"}
              </button>
              <button type="button" role="menuitem" className="btn-danger" onClick={remove}>
                채널 삭제
              </button>
            </>
          )}
        </div>
      )}
      <EditChannelDialog channel={channel} open={editing} onClose={() => setEditing(false)} />
    </div>
  );
}

function EditChannelDialog({ channel, open, onClose }: { channel: ChannelDTO; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setName(channel.name);
      setTopic(channel.topic);
      setErr(null);
    }
  }, [open, channel.name, channel.topic]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await http.patch(`/api/channels/${channel.id}`, { name, topic });
      await qc.invalidateQueries({ queryKey: keys.channels });
      onClose();
    } catch (e) {
      setErr(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} label="채널 정보 바꾸기">
      <form onSubmit={submit}>
        <h2>채널 정보</h2>
        {err && !fieldError(err, "name") ? <p className="form-error">{errorMessage(err)}</p> : null}
        <Field id="edit-channel-name" label="이름" error={fieldError(err, "name")}>
          <input id="edit-channel-name" className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} disabled={channel.isDefault} autoComplete="off" />
        </Field>
        <Field id="edit-channel-topic" label="설명" error={fieldError(err, "topic")}>
          <input id="edit-channel-topic" className="input" maxLength={200} value={topic} onChange={(e) => setTopic(e.target.value)} autoComplete="off" />
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary">
            저장
          </button>
        </div>
      </form>
    </Dialog>
  );
}
